/**
 * Access & Benefits Engine™ — resolves WHICH benefit a person gets, then hands
 * the arithmetic to the Pricing Engine.
 *
 *   MRP → campaign → benefit eligibility → coupon → scholarship
 *       → partner → institution → referral → final amount
 *
 * The engine never calculates money itself: it produces `DiscountRule`
 * candidates and passes them to `calculatePrice` as `entitlements`, so there is
 * still exactly one place prices are computed.
 */

import { COUPONS, PARTNERS, SCHOLARSHIPS, REGIONS, DEFAULT_REGION } from "../pricing-config";
import { findCoupon, isPriceableInRegion, resolveRuleForRegion } from "../coupon-engine";
import { calculatePrice } from "../pricing-engine";
import type {
  DiscountRule,
  PricingRequest,
  PricingResult,
  RegionCode,
} from "../pricing-types";
import { BENEFIT_VERSION, benefitRule, benefitTypeForCode, INVITATION_ALPHABET } from "./benefit-config";
import {
  benefitLayer,
  dedupeAgainstEntitlements,
  rankBenefits,
  supersededReason,
} from "./benefit-policy";
import {
  detectFraudSignals,
  hasBlockingSignal,
  isBenefitAllowedInRegion,
  reject,
  summariseEligibility,
  validateGrant,
  validateInvitation,
} from "./benefit-validator";
import type {
  BenefitCandidate,
  BenefitDecision,
  BenefitEligibility,
  BenefitGrant,
  BenefitType,
  FraudSignal,
  InvitationCode,
  RedemptionContext,
} from "./benefit-types";

export { BENEFIT_VERSION };

/** An eligibility object for someone with nothing granted. */
export function emptyEligibility(
  overrides: Partial<BenefitEligibility> = {},
): BenefitEligibility {
  return {
    userId: null,
    email: null,
    scholarship: false,
    partnerChild: false,
    institution: false,
    referral: false,
    ambassador: false,
    staff: false,
    admin: false,
    campaigns: [],
    invitations: [],
    institutionIds: [],
    partnerId: null,
    grants: [],
    ...overrides,
  };
}

/** Row shape returned by `get_my_benefit_eligibility()`. */
export interface EligibilityRpcPayload {
  ok?: boolean;
  userId?: string | null;
  email?: string | null;
  isAdmin?: boolean;
  grants?: Array<Record<string, unknown>>;
  invitations?: string[];
  institutionIds?: string[];
  partnerId?: string | null;
}

/** Adapt the database payload into an eligibility object. */
export function eligibilityFromRpc(
  payload: EligibilityRpcPayload | null | undefined,
  email?: string | null,
): BenefitEligibility {
  if (!payload || payload.ok === false) return emptyEligibility({ email: email ?? null });

  const grants: BenefitGrant[] = (payload.grants ?? []).map((g) => ({
    id: String(g.id ?? ""),
    benefitType: String(g.benefitType ?? "SCHOLARSHIP") as BenefitType,
    benefitId: String(g.benefitId ?? ""),
    status: (g.status ?? "active") as BenefitGrant["status"],
    partnerId: (g.partnerId as string) ?? null,
    institutionId: (g.institutionId as string) ?? null,
    validFrom: (g.validFrom as string) ?? null,
    validUntil: (g.validUntil as string) ?? null,
    source: (g.source as string) ?? null,
  }));

  return summariseEligibility({
    userId: payload.userId ?? null,
    email: email ?? payload.email ?? null,
    admin: payload.isAdmin === true,
    campaigns: [],
    invitations: payload.invitations ?? [],
    institutionIds: (payload.institutionIds ?? []).map(String),
    partnerId:
      payload.partnerId ??
      grants.find((g) => g.benefitType === "PARTNER")?.partnerId ??
      null,
    grants,
  });
}

/** Find the priced discount rule a benefit id refers to. */
export function ruleForBenefitId(benefitId: string): DiscountRule | null {
  const id = String(benefitId || "").trim().toUpperCase();
  return (
    findCoupon(id) ??
    SCHOLARSHIPS.find((s) => s.id === id) ??
    PARTNERS.find((p) => p.id === id) ??
    COUPONS.find((c) => c.id === id) ??
    null
  );
}

function candidateFrom(
  benefitType: BenefitType,
  benefitId: string,
  source: BenefitCandidate["source"],
  region: RegionCode,
): BenefitCandidate | null {
  const base = ruleForBenefitId(benefitId);
  if (!base) return null;
  if (!isPriceableInRegion(base, region)) return null;
  const resolved = resolveRuleForRegion(base, region);
  const layer = benefitLayer(benefitType);
  return {
    benefitType,
    benefitId: base.id,
    layer,
    // The benefit decides the layer, so an entitlement always prices through
    // its own layer even when the underlying catalogue row is a coupon.
    rule: { ...resolved, layer },
    source,
    priority: benefitRule(benefitType).priority,
  };
}

export interface BenefitResolutionInput {
  eligibility?: BenefitEligibility;
  /** A code the buyer typed (legacy coupon, campaign, or invitation). */
  code?: string | null;
  /** An invitation row the caller already loaded for `code`. */
  invitation?: InvitationCode | null;
  region?: RegionCode;
  now?: Date | string;
  isAdmin?: boolean;
  redemption?: RedemptionContext;
}

/**
 * Decide which benefit applies. Deterministic: same input plus same `now`
 * always yields the same decision.
 */
export function resolveBenefits(input: BenefitResolutionInput = {}): BenefitDecision {
  const now = input.now
    ? input.now instanceof Date
      ? input.now
      : new Date(input.now)
    : new Date();
  const region = (input.region ?? DEFAULT_REGION) as RegionCode;
  const reg = REGIONS[region] ?? REGIONS[DEFAULT_REGION];
  const eligibility = input.eligibility ?? emptyEligibility();
  const isAdmin = input.isAdmin === true || eligibility.admin === true;

  const considered: BenefitCandidate[] = [];
  const skipped: BenefitDecision["skipped"] = [];
  const notes: string[] = [];
  let rejection: BenefitDecision["rejection"] = null;

  // ---- Account entitlements (no code needed) ----------------------------
  for (const grant of eligibility.grants) {
    const check = validateGrant(grant, { now, isAdmin });
    const candidate = candidateFrom(
      grant.benefitType,
      grant.benefitId,
      "grant",
      region,
    );
    if (!candidate) {
      notes.push(`grant ${grant.benefitId} has no priced rule in ${region}`);
      continue;
    }
    if (!check.ok) {
      skipped.push({
        candidate,
        reason: check.rejection?.reason ?? "grant not valid",
      });
      continue;
    }
    if (!isBenefitAllowedInRegion(grant.benefitType, reg.allowedLayers, region)) {
      skipped.push({ candidate, reason: `not enabled for region ${region}` });
      continue;
    }
    considered.push(candidate);
  }

  // ---- An invitation code the buyer typed --------------------------------
  if (input.invitation) {
    const check = validateInvitation(input.invitation, {
      now,
      userId: eligibility.userId,
      email: eligibility.email,
      isAdmin,
    });
    const candidate = candidateFrom(
      input.invitation.benefitType,
      input.invitation.benefitId,
      "invitation",
      region,
    );
    if (candidate && check.ok) considered.push(candidate);
    else if (candidate) {
      rejection = check.rejection;
      skipped.push({
        candidate,
        reason: check.rejection?.reason ?? "invitation not valid",
      });
    } else {
      rejection = reject("not_found");
    }
  }

  // ---- A legacy / campaign code the buyer typed -------------------------
  const typed = String(input.code ?? "").trim().toUpperCase();
  if (typed && !input.invitation) {
    const type = benefitTypeForCode(typed);
    const candidate = candidateFrom(type, typed, "coupon", region);
    if (!candidate) {
      // Not a known configured code — the Pricing Engine still validates it
      // against the database, so this is a note, not a rejection.
      notes.push(`code ${typed} is not in the benefit catalogue; deferring to coupons table`);
    } else if (!isBenefitAllowedInRegion(type, reg.allowedLayers, region)) {
      skipped.push({ candidate, reason: `not enabled for region ${region}` });
      rejection = reject("region_not_allowed");
    } else {
      considered.push(candidate);
    }
  }

  // ---- Fraud + usage caps ------------------------------------------------
  let fraudSignals: FraudSignal[] = [];
  const ranked = rankBenefits(dedupeAgainstEntitlements(considered).kept);
  skipped.push(...dedupeAgainstEntitlements(considered).skipped);

  let applied: BenefitCandidate | null = null;
  for (const candidate of ranked) {
    const signals = input.redemption
      ? detectFraudSignals(candidate.benefitType, candidate.benefitId, input.redemption)
      : [];
    if (hasBlockingSignal(signals)) {
      fraudSignals = [...fraudSignals, ...signals];
      skipped.push({ candidate, reason: "blocked by abuse checks" });
      rejection = reject("already_redeemed");
      continue;
    }
    fraudSignals = [...fraudSignals, ...signals];
    applied = candidate;
    break;
  }

  for (const c of ranked) {
    if (applied && c !== applied && !skipped.some((s) => s.candidate === c)) {
      skipped.push({ candidate: c, reason: supersededReason(c, applied) });
    }
  }

  if (applied) rejection = null;

  return {
    benefitVersion: BENEFIT_VERSION,
    applied,
    considered: ranked,
    skipped,
    rejection,
    fraudSignals,
    notes,
  };
}

export interface BenefitPricingResult {
  decision: BenefitDecision;
  pricing: PricingResult;
}

/**
 * Price an order through the Access & Benefits Engine. This is the entry point
 * callers should use; it never bypasses the Pricing Engine.
 */
export function priceWithBenefits(
  input: BenefitResolutionInput & PricingRequest = {},
): BenefitPricingResult {
  const decision = resolveBenefits(input);
  const entitlements = decision.applied ? [decision.applied.rule] : [];

  // A typed code still travels the normal coupon path so the database stays
  // authoritative for redemption counts. Entitlements are additive candidates.
  const pricing = calculatePrice({
    ...input,
    entitlements,
    isAdmin: input.isAdmin === true || input.eligibility?.admin === true,
    userId: input.userId ?? input.eligibility?.userId ?? null,
  });

  return { decision, pricing };
}

/**
 * Format a unique invitation code, e.g. `FNF-92JFKQ`. Uniqueness is enforced by
 * the database; this helper exists so the admin hub can preview the shape and
 * tests can generate codes deterministically.
 */
export function formatInvitationCode(
  prefix: string,
  random: () => number = Math.random,
  length = 6,
): string {
  const clean = String(prefix || "INV")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 8) || "INV";
  let body = "";
  for (let i = 0; i < length; i += 1) {
    body += INVITATION_ALPHABET[Math.floor(random() * INVITATION_ALPHABET.length)];
  }
  return `${clean}-${body}`;
}
